import { useMemo } from 'react';

import {
  loopsOnLayer,
  selectionOf,
  summarizeSelection,
} from '@frontend/viewer-core';

import type { DrawingModel, DxfViewerSession } from '../session/types';
import { useReadableStore } from '../session/useSessionState';
import {
  formatArea,
  formatLength,
  formatSize,
  type MeasurementFormat,
} from '../utils/formatMeasurement';

export type DxfDetailsOverlayState = {
  size: string;
  hasDetail: boolean;
  heading: string;
  count: number;
  length: string;
  area: string | null;
};

/**
 * Connects `DxfDetailsOverlay` to the session. Call only under a ready drawing —
 * empty bounds returns `null` so the overlay can hide, which is not a load state.
 */
export const useDxfDetailsOverlay = (
  session: DxfViewerSession,
  model: DrawingModel,
  format: MeasurementFormat,
): DxfDetailsOverlayState | null => {
  const hoveredLayer = useReadableStore(session.hoveredLayer);

  // A hovered layer takes precedence: the user is asking about it right now.
  // Memoised because `countRuns` flood-fills the welded graph, and this hook
  // would otherwise redo that walk on every render this component takes for
  // any reason, not only the ones where the selection or hover actually moved.
  const summary = useMemo(
    () =>
      hoveredLayer === null
        ? session.selection.summarize()
        : summarizeSelection(
            selectionOf(loopsOnLayer(model.loops, hoveredLayer)),
            model.loops,
            model.chunkGraph,
          ),
    [hoveredLayer, model, session.selection],
  );

  const bounds = model.bounds;
  if (!bounds) {
    return null;
  }

  const [minX, minY, maxX, maxY] = bounds;

  const heading =
    hoveredLayer === null
      ? 'Selection'
      : model.scene.layers[hoveredLayer]?.name || 'Layer';
  const hasDetail = summary.count > 0;

  return {
    size: formatSize(maxX - minX, maxY - minY, format),
    hasDetail,
    heading,
    count: summary.count,
    length: hasDetail ? formatLength(summary.length, format) : '',
    area:
      hasDetail && summary.area !== null
        ? formatArea(summary.area, format)
        : null,
  };
};
